import type { CalendarDate } from '@internationalized/date';
import { today, getLocalTimeZone } from '@internationalized/date';
import { useCalendarCell } from '@react-aria/calendar';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import * as React from 'react';
import { cn } from '@utils';

type CalendarCellProps = {
	state: CalendarState | RangeCalendarState;
	date: CalendarDate;
	currentMonth: CalendarDate;
};

const isRangeCalendarState = (state: CalendarState | RangeCalendarState): state is RangeCalendarState => {
	return 'highlightedRange' in state;
};

export const CalendarCell: React.VoidFunctionComponent<CalendarCellProps> = ({ state, date, currentMonth }) => {
	const ref: React.MutableRefObject<HTMLDivElement | null> = React.useRef(null);

	const { cellProps, buttonProps, isSelected, isDisabled, isUnavailable, isOutsideVisibleRange, formattedDate } =
		useCalendarCell({ date }, state, ref);

	const isOutsideMonth: boolean = date.month !== currentMonth.month;

	const isToday: boolean = date.compare(today(getLocalTimeZone())) === 0 && !isSelected;

	let isRangeStart: boolean = false;
	let isRangeEnd: boolean = false;
	let isInRange: boolean = false;

	if (isRangeCalendarState(state) && state.highlightedRange) {
		const { start, end } = state.highlightedRange;
		isRangeStart = date.compare(start) === 0;
		isRangeEnd = date.compare(end) === 0;
		isInRange = isSelected && !isRangeStart && !isRangeEnd;
	}

	const isEndpointSelected: boolean = isSelected && (isRangeStart || isRangeEnd || !isRangeCalendarState(state));

	return (
		<td {...cellProps} className="p-0 text-center align-middle">
			<div
				{...buttonProps}
				ref={ref}
				data-testid={`calendar-date-picker-cell-${date.toString()}`}
				className={cn(
					'mx-auto flex size-9 cursor-pointer items-center justify-center rounded-xl text-sm font-medium transition-colors outline-none select-none',
					'focus-visible:ring-2 focus-visible:ring-highlightSelected focus-visible:ring-offset-1',
					!isDisabled && !isUnavailable && !isToday && 'hover:bg-bgPrimary hover:text-primary',
					isOutsideMonth && !isSelected && 'text-disabled',
					!isOutsideMonth && !isSelected && 'text-textBlack',
					isToday && 'relative z-10 ring-2 ring-accent hover:bg-accent/10',
					isInRange && 'rounded-none bg-bgPrimary text-textBlack hover:bg-bgPrimary',
					isEndpointSelected && 'bg-highlightSelected text-white hover:bg-highlightSelected hover:text-white',
					isRangeStart && !isRangeEnd && 'rounded-r-none',
					isRangeEnd && !isRangeStart && 'rounded-l-none',
					isUnavailable && 'cursor-not-allowed text-textDisabled line-through',
					isDisabled && 'cursor-not-allowed text-textDisabled hover:bg-transparent',
					isOutsideVisibleRange && 'invisible'
				)}
			>
				{formattedDate}
			</div>
		</td>
	);
};
